import {
  createThemeContract,
  createTheme,
  globalFontFace,
} from "@vanilla-extract/css";
import {
  colors,
  SYSTEM_FONT_STACK,
  letterSpacing,
  lineHeight,
  fontSize,
  fontWeight,
  radii,
  borderStyle,
  borderWidth,
  space,
  zIndex,
} from "./tokens";

const fontInterName = "Inter";

globalFontFace(fontInterName, {
  src: `url(https://fonts.googleapis.com/css?family=Inter)`,
  fontWeight: [100, 200, 300, 400, 500, 600, 700, 800, 900],
  fontStyle: `normal`,
  fontDisplay: `swap`,
});

export const boxShadow = {
  xs: `0 0 0 1px rgba(0, 0, 0, 0.05)`,
  sm: `0 1px 2px 0 rgba(0, 0, 0, 0.05)`,
  base: `0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)`,
  md: `0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)`,
  lg: `0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)`,
  xl: `0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)`,
  "2xl": `0 25px 50px -12px rgba(0, 0, 0, 0.25)`,
  inset: `inset 0 2px 4px 0 rgba(0,0,0,0.06)`,
  primaryOutline: `0 0 0 2px ${colors.primary200}`,
  none: `none`,
  "dark-lg": `rgba(0, 0, 0, 0.1) 0px 0px 0px 1px, rgba(0, 0, 0, 0.2) 0px 5px 10px, rgba(0, 0, 0, 0.4) 0px 15px 40px`,
};

export const themeContractTemplate = {
  colors: {
    accent: ``,
    accentText: ``,
    primary: ``,
    body: ``,
    background: ``,
    link: ``,
    linkHover: ``,
    text: ``,
    textInverse: ``,
    textSecondary: ``,
    textMuted: ``,
    textDanger: ``,
    textWarning: ``,
    textPlaceholder: ``,
    textSuccess: ``,
    rewardBg: ``,
    rewardContent: ``,
    cardBg: ``,
    inputBorder: ``,
    inputBorderFocus: ``,
    inputBg: ``,
    inputDangerBorder: "",
    inputDangerBg: "",
    inputDisabledBg: "",
    inputDisabledText: "",
    progressBg: ``,
    progressValue: ``,
    progressCursor: ``,
    trackBg: ``,
    divider: ``,
    menuItemBg: ``,
    menuItemBgSelected: ``,
    menuItemBgHovered: ``,
    menuItemBgActive: ``,
    // ==== TagButton
    tagButtonText: ``,
    tagButtonBg: ``,
    tagButtonBgHovered: ``,
    // ==== MeshButton
    meshButtonSolidPrimaryBg: ``,
    meshButtonSolidPrimaryBgHovered: ``,
    meshButtonSolidPrimaryText: ``,
    meshButtonSolidSecondaryBg: ``,
    meshButtonSolidSecondaryBgHovered: ``,
    meshButtonSolidSecondaryText: ``,
    meshButtonGhostText: ``,
    meshButtonGhostTextHovered: ``,
    // ==== MeshStakingSliderInfo
    meshStakingSliderInfoPrimaryText: ``,
    meshStakingSliderInfoSecondaryText: ``,
    meshStakingSliderInfoSecondaryTextActive: ``,
    // ==== MeshTab
    meshTabText: ``,
    // ==== MeshTableCell
    meshTableCellText: ``,
    // ==== MeshTableHeaderAction
    meshTableHeaderActionText: ``,
    meshTableHeaderActionSecondaryText: ``,
    meshTableCellBorder: ``,
    // ====
    skeletonBg: ``,
    overflowShadowBg: ``,
    ...colors,
  },
  font: {
    body: ``,
  },
  space: {
    "0": ``,
    "1": ``,
    "2": ``,
    "3": ``,
    "4": ``,
    "5": ``,
    "6": ``,
    "7": ``,
    "8": ``,
    "9": ``,
    "10": ``,
    "11": ``,
    "12": ``,
    "13": ``,
    "14": ``,
    "15": ``,
    "16": ``,
    "17": ``,
    "18": ``,
    "19": ``,
    "20": ``,
    "21": ``,
    "22": ``,
    "23": ``,
    "24": ``,
    "25": ``,
    "26": ``,
    "27": ``,
    "28": ``,
    "29": ``,
    "30": ``,
    auto: ``,
    full: ``,
    fit: ``,
    max: ``,
    min: ``,
    viewHeight: ``,
    viewWidth: ``,
    containerSm: ``,
    containerMd: ``,
    containerLg: ``,
    containerXl: ``,
    prose: ``,
    none: ``,
  },
  borderWidth: {
    none: ``,
    sm: ``,
    base: ``,
    md: ``,
    lg: ``,
    xl: ``,
  },
  borderStyle: {
    none: ``,
    solid: ``,
    dotted: ``,
    dashed: ``,
    groove: ``,
    ridge: ``,
    hidden: ``,
    double: ``,
    inset: ``,
    outset: ``,
    unset: ``,
  },
  boxShadow: {
    xs: ``,
    sm: ``,
    base: ``,
    md: ``,
    lg: ``,
    xl: ``,
    "2xl": ``,
    inset: ``,
    primaryOutline: ``,
    none: ``,
    "dark-lg": ``,
  },
  radii: {
    none: ``,
    sm: ``,
    base: ``,
    md: ``,
    lg: ``,
    xl: ``,
    "2xl": ``,
    "3xl": ``,
    "4xl": ``,
    full: ``,
  },
  letterSpacing: {
    tighter: ``,
    tight: ``,
    normal: ``,
    wide: ``,
    wider: ``,
    widest: ``,
  },
  lineHeight: {
    normal: ``,
    none: ``,
    shorter: ``,
    short: ``,
    base: ``,
    tall: ``,
    taller: ``,
  },
  fontWeight: {
    hairline: ``,
    thin: ``,
    light: ``,
    normal: ``,
    medium: ``,
    semibold: ``,
    bold: ``,
    extrabold: ``,
    black: ``,
  },
  fontSize: {
    "3xs": ``,
    "2xs": ``,
    xs: ``,
    sm: ``,
    md: ``,
    lg: ``,
    xl: ``,
    "2xl": ``,
    "3xl": ``,
    "4xl": ``,
    "5xl": ``,
    "6xl": ``,
    "7xl": ``,
    "8xl": ``,
    "9xl": ``,
    "10xl": ``,
    "11xl": ``,
    "12xl": ``,
    "13xl": ``,
    "14xl": ``,
    "15xl": ``,
  },
  zIndex: {
    "-1": ``,
    "0": ``,
    "10": ``,
    "20": ``,
    "30": ``,
    "40": ``,
    "50": ``,
    "100": ``,
    auto: ``,
  },
} as const;

export type ThemeContractValues = typeof themeContractTemplate;

// Enforce a theme contract so that light/dark/xxx themes will have the same properties
export const themeVars = createThemeContract(themeContractTemplate);

export const commonVars = {
  font: {
    body: [fontInterName, SYSTEM_FONT_STACK].join(`, `),
  },
  fontSize,
  fontWeight,
  letterSpacing,
  lineHeight,
  space,
  boxShadow,
  radii,
  borderWidth,
  borderStyle,
  zIndex,
};

export const lightThemeClass = createTheme(themeVars, {
  ...commonVars,
  colors: {
    accent: colors.primary500,
    accentText: "#2C3137",
    primary: colors.primary500,
    body: colors.gray800,
    background: colors.gray100,
    link: colors.blue800,
    linkHover: colors.blue600,
    text: "#2C3137",
    textMuted: `#8C9196`,
    textInverse: `#EEF2F8`,
    textSecondary: "#697584",
    textDanger: colors.red500,
    textWarning: colors.orange300,
    textSuccess: colors.green400,
    textPlaceholder: `#A2AEBB`,
    rewardBg: "#E5FFE4",
    rewardContent: "#36BB35",
    cardBg: "#F5F7FB",
    inputBorder: "#D1D6DD",
    inputBorderFocus: "#D1D6DD",
    inputBg: "#ffffff",
    inputDangerBorder: "#FF8080",
    inputDangerBg: "#FFDBDB",
    inputDisabledBg: "#CBD3DD",
    inputDisabledText: "#8895A3",
    progressBg: `#EEF2F8`,
    progressValue: `#697584`,
    progressCursor: `#2C3137`,
    divider: `#D9D9D9`,
    menuItemBg: `#ffffff`,
    menuItemBgSelected: `#dce4f0`,
    menuItemBgHovered: `#EEF2F8`,
    menuItemBgActive: `#DDE4ED`,
    // ==== TagButton
    tagButtonText: `#2C3137`,
    tagButtonBg: `#dce4f0`,
    tagButtonBgHovered: `#EEF2F8`,
    // ==== MeshButton
    meshButtonSolidPrimaryBg: `#2C3137`,
    meshButtonSolidPrimaryBgHovered: `#A2AEBB`,
    meshButtonSolidPrimaryText: colors.white,
    meshButtonSolidSecondaryBg: colors.gray100,
    meshButtonSolidSecondaryBgHovered: colors.gray100,
    meshButtonSolidSecondaryText: `#2C3137`,
    meshButtonGhostText: `#2C3137`,
    meshButtonGhostTextHovered: `#697584`,
    // ==== MeshStakingSliderInfo
    meshStakingSliderInfoPrimaryText: `#2C3137`,
    meshStakingSliderInfoSecondaryText: `#697584`,
    meshStakingSliderInfoSecondaryTextActive: colors.green400,
    // ==== MeshTab
    meshTabText: `#2C3137`,
    meshTableCellText: `#2C3137`,
    // ==== MeshTableHeaderAction
    meshTableHeaderActionText: `#697584`,
    meshTableHeaderActionSecondaryText: `#A2AEBB`,
    meshTableCellBorder: `#E1DBEB`,
    skeletonBg: `#DDE4ED`,
    trackBg: `#EBEFF5`,
    overflowShadowBg:
      "linear-gradient(0deg, rgba(255,255,255,1) 6%, rgba(255,255,255,0.95) 16%, rgba(255,255,255,0.85) 24%, rgba(255,255,255,0.75) 32%, rgba(255,255,255,0.65) 48%, rgba(255,255,255,0.4) 65%, rgba(255,255,255,0.2) 80%, rgba(255,255,255,0.1) 95%)",
    ...colors,
  },
});

export const darkThemeClass = createTheme(themeVars, {
  ...commonVars,
  colors: {
    accent: colors.primary400,
    accentText: "#EEF2F8",
    primary: colors.primary400,
    body: colors.gray300,
    background: colors.gray800,
    link: colors.blue300,
    linkHover: colors.blue400,
    text: "#EEF2F8",
    textMuted: "#BCC4D1",
    textInverse: `#2C3137`,
    textSecondary: "#A7B4C2",
    textDanger: colors.red400,
    textWarning: colors.orange200,
    textSuccess: "#AEFFAB",
    textPlaceholder: `#8895A3`,
    rewardBg: "#2F4139",
    rewardContent: "#AEFFAB",
    cardBg: "#1D2024",
    inputBorder: "#434B55",
    inputBorderFocus: "#697584",
    inputBg: "#0f172a",
    inputDangerBorder: "#FFD0D0",
    inputDangerBg: "#E17171",
    inputDisabledBg: "#A7B4C2",
    inputDisabledText: "#697584",
    progressBg: "#16191c",
    progressValue: `#A7B4C2`,
    progressCursor: `#EEF2F8`,
    divider: colors.whiteAlpha200,
    menuItemBg: `#1D2024`,
    menuItemBgSelected: `#2e3339`,
    menuItemBgHovered: `#25292E`,
    menuItemBgActive: `#2C3137`,
    // ==== TagButton
    tagButtonText: `#DAD5E3`,
    tagButtonBg: `#434B55`,
    tagButtonBgHovered: `#27272B`,
    // ==== MeshButton
    meshButtonSolidPrimaryBg: `#DAD5E3`,
    meshButtonSolidPrimaryBgHovered: `#E2E2E2`,
    meshButtonSolidPrimaryText: `#0E0E0F`,
    meshButtonSolidSecondaryBg: `#1E1E1F`,
    meshButtonSolidSecondaryBgHovered: `#1E1E1F`,
    meshButtonSolidSecondaryText: `#DAD5E3`,
    meshButtonGhostText: `#85858E`,
    meshButtonGhostTextHovered: `#E1DBEB`,
    // ==== MeshStakingSliderInfo
    meshStakingSliderInfoPrimaryText: `#8895A3`,
    meshStakingSliderInfoSecondaryText: `#A7B4C2`,
    meshStakingSliderInfoSecondaryTextActive: `#AEFFAB`,
    meshTabText: `#E2E2E2`,
    meshTableCellText: `#EEF2F8`,
    // ==== MeshTableHeaderAction
    meshTableHeaderActionText: `#EEF2F8`,
    meshTableHeaderActionSecondaryText: `#A7B4C2`,
    meshTableCellBorder: `#131313`,
    skeletonBg: `#3B434D`,
    trackBg: `#49525E`,
    overflowShadowBg:
      "linear-gradient(to bottom, rgba(29, 32, 36, 0), rgba(29, 32, 36, 0.6))",
    ...colors,
  },
});

export const meshLightThemeClass = createTheme(themeVars, {
  ...commonVars,
  colors: {
    ...colors,
    accent: colors.primary500,
    accentText: "#2C3137",
    primary: colors.primary400,
    black: "#131313",
    body: colors.gray800,
    background: colors.gray100,
    link: colors.blue800,
    linkHover: colors.blue600,
    text: "#2C3137",
    textMuted: `#8C9196`,
    textInverse: `#EEF2F8`,
    textSecondary: "#697584",
    textDanger: colors.red500,
    textWarning: colors.orange300,
    textSuccess: colors.green400,
    textPlaceholder: `#A2AEBB`,
    rewardBg: "#E5FFE4",
    rewardContent: "#36BB35",
    cardBg: "#ffffff",
    inputBorder: "#D1D6DD",
    inputBorderFocus: "#D1D6DD",
    inputBg: "#ffffff",
    inputDangerBorder: "#FF8080",
    inputDangerBg: "#FFDBDB",
    inputDisabledBg: "#CBD3DD",
    inputDisabledText: "#8895A3",
    progressBg: `#EEF2F8`,
    progressValue: `#697584`,
    progressCursor: `#2C3137`,
    divider: `#D9D9D9`,
    menuItemBg: `#ffffff`,
    menuItemBgSelected: `#dce4f0`,
    menuItemBgHovered: `#EEF2F8`,
    menuItemBgActive: `#DDE4ED`,
    tagButtonText: `#2C3137`,
    tagButtonBg: `#dce4f0`,
    tagButtonBgHovered: `#EEF2F8`,
    // ==== MeshButton
    meshButtonSolidPrimaryBg: `#2C3137`,
    meshButtonSolidPrimaryBgHovered: `#A2AEBB`,
    meshButtonSolidPrimaryText: colors.white,
    meshButtonSolidSecondaryBg: colors.gray100,
    meshButtonSolidSecondaryBgHovered: colors.gray100,
    meshButtonSolidSecondaryText: `#2C3137`,
    meshButtonGhostText: `#2C3137`,
    meshButtonGhostTextHovered: `#697584`,
    // ==== MeshStakingSliderInfo
    meshStakingSliderInfoPrimaryText: `#2C3137`,
    meshStakingSliderInfoSecondaryText: `#697584`,
    meshStakingSliderInfoSecondaryTextActive: colors.green400,
    // ==== MeshTab
    meshTabText: `#2C3137`,
    meshTableCellText: `#2C3137`,
    // ==== MeshTableHeaderAction
    meshTableHeaderActionText: `#2C3137`,
    meshTableHeaderActionSecondaryText: `#697584`,
    meshTableCellBorder: `#E1DBEB`,
    // ====
    skeletonBg: `#DDE4ED`,
    trackBg: `#EBEFF5`,
    overflowShadowBg:
      "linear-gradient(0deg, rgba(255,255,255,1) 6%, rgba(255,255,255,0.95) 16%, rgba(255,255,255,0.85) 24%, rgba(255,255,255,0.75) 32%, rgba(255,255,255,0.65) 48%, rgba(255,255,255,0.4) 65%, rgba(255,255,255,0.2) 80%, rgba(255,255,255,0.1) 95%)",
    // Override colors
    gray50: "#F7FAFC",
    gray100: "#E1DBEB",
    gray200: "#858591",
    gray300: "#3F3F48",
    gray400: "#2E2E34",
    gray500: "#27272B",
    gray600: "#202023",
    gray700: "#1A1A1D",
    gray800: "#101012",
    gray900: "#0C0C0D",
  },
});

export const meshDarkThemeClass = createTheme(themeVars, {
  ...commonVars,
  colors: {
    ...colors,
    accent: colors.primary400,
    accentText: "#0E0E0F",
    primary: colors.primary400,
    black: "#131313",
    body: "#1E1E1F",
    background: colors.gray800,
    link: colors.blue300,
    linkHover: colors.blue400,
    text: "#DAD5E3",
    textMuted: "#AEA7BC",
    textInverse: `#2C3137`,
    textSecondary: "#85858E",
    textDanger: "#E35B5B",
    textWarning: colors.orange200,
    textPlaceholder: `#E2E2E2`,
    textSuccess: "#C0EEA4",
    rewardBg: "#2F4139",
    rewardContent: "#AEFFAB",
    cardBg: "#111113",
    inputBorder: "#3D3D42",
    inputBorderFocus: "#D1D6DD",
    inputBg: "#1D2024",
    inputDangerBorder: "#FFD0D0",
    inputDangerBg: "#E35B5B",
    inputDisabledBg: "#A7B4C2",
    inputDisabledText: "#697584",
    progressBg: `#C0EEA4`,
    progressValue: `#A7B4C2`,
    progressCursor: `#EEF2F8`,
    divider: "#201E25",
    menuItemBg: `#1D2024`,
    menuItemBgSelected: `#2e3339`,
    menuItemBgHovered: `#25292E`,
    menuItemBgActive: `#2C3137`,
    tagButtonText: `#DAD5E3`,
    tagButtonBg: `#202023`,
    tagButtonBgHovered: `#27272B`,
    // ==== MeshButton
    meshButtonSolidPrimaryBg: `#DAD5E3`,
    meshButtonSolidPrimaryBgHovered: `#E2E2E2`,
    meshButtonSolidPrimaryText: `#0E0E0F`,
    meshButtonSolidSecondaryBg: `#1E1E1F`,
    meshButtonSolidSecondaryBgHovered: `#1E1E1F`,
    meshButtonSolidSecondaryText: `#DAD5E3`,
    meshButtonGhostText: `#85858E`,
    meshButtonGhostTextHovered: `#E1DBEB`,
    // ==== MeshStakingSliderInfo
    meshStakingSliderInfoPrimaryText: `#E2E2E2`,
    meshStakingSliderInfoSecondaryText: `#85858E`,
    meshStakingSliderInfoSecondaryTextActive: `#C0EEA4`,
    // ==== MeshTab
    meshTabText: `#E2E2E2`,
    meshTableCellText: `#E2E2E2`,
    // ==== MeshTableHeaderAction
    meshTableHeaderActionText: colors.white,
    meshTableHeaderActionSecondaryText: `#E2E2E2`,
    meshTableCellBorder: `#131313`,
    // ====
    skeletonBg: `#3B434D`,
    trackBg: `#49525E`,
    overflowShadowBg:
      "linear-gradient(0deg, rgba(17,17,19,1) 5%, rgba(9,9,121,0) 35%)",
    // Override colors
    gray50: "#F7FAFC",
    gray100: "#E1DBEB",
    gray200: "#858591",
    gray300: "#3F3F48",
    gray400: "#2E2E34",
    gray500: "#27272B",
    gray600: "#202023",
    gray700: "#1A1A1D",
    gray800: "#101012",
    gray900: "#0C0C0D",
  },
});

export const nobleLightThemeClass = createTheme(themeVars, {
  ...commonVars,
  colors: {
    ...colors,
    accent: colors.primary500,
    accentText: `#2C3137`,
    primary: `#6B7FFF`,
    body: `#f6f6ff`,
    background: colors.gray100,
    link: colors.blue800,
    linkHover: colors.blue600,
    text: `#020418`,
    textMuted: "#4A4C5F",
    textInverse: `#F5F5F5`,
    textSecondary: `#6D6D88`,
    textDanger: colors.red500,
    textWarning: colors.orange300,
    textSuccess: `#54B375`,
    textPlaceholder: `#A2AEBB`,
    rewardBg: `#E5FFE4`,
    rewardContent: `#36BB35`,
    cardBg: `#ffffff`,
    inputBorder: `#D5D5E8`,
    inputBorderFocus: `#AAAABF`,
    inputBg: `#ffffff`,
    inputDangerBorder: `#FF8080`,
    inputDangerBg: `#FFDBDB`,
    inputDisabledBg: `#CBD3DD`,
    inputDisabledText: `#8895A3`,
    progressBg: `#D5D5E8`,
    progressValue: `#6B7FFF`,
    progressCursor: `#2C3137`,
    divider: `#DCDEED`,
    menuItemBg: `#ffffff`,
    menuItemBgSelected: `#dce4f0`,
    menuItemBgHovered: `#EEF2F8`,
    menuItemBgActive: `#DDE4ED`,
    skeletonBg: `#DDE4ED`,
    trackBg: `#EBEFF5`,
    tagButtonText: `#2C3137`,
    tagButtonBg: `#dce4f0`,
    tagButtonBgHovered: `#EEF2F8`,
    // ==== MeshButton
    meshButtonSolidPrimaryBg: `#2C3137`,
    meshButtonSolidPrimaryBgHovered: `#A2AEBB`,
    meshButtonSolidPrimaryText: colors.white,
    meshButtonSolidSecondaryBg: colors.gray100,
    meshButtonSolidSecondaryBgHovered: colors.gray100,
    meshButtonSolidSecondaryText: `#2C3137`,
    meshButtonGhostText: `#2C3137`,
    meshButtonGhostTextHovered: `#697584`,
    // ==== MeshStakingSliderInfo
    meshStakingSliderInfoPrimaryText: `#2C3137`,
    meshStakingSliderInfoSecondaryText: `#697584`,
    meshStakingSliderInfoSecondaryTextActive: colors.green400,
    // ==== MeshTab
    meshTabText: `#2C3137`,
    meshTableCellText: `#2C3137`,
    // ==== MeshTableHeaderAction
    meshTableHeaderActionText: `#2C3137`,
    meshTableHeaderActionSecondaryText: `#697584`,
    meshTableCellBorder: `#E1DBEB`,
    // ====
    overflowShadowBg:
      "linear-gradient(0deg, rgba(255,255,255,1) 6%, rgba(255,255,255,0.95) 16%, rgba(255,255,255,0.85) 24%, rgba(255,255,255,0.75) 32%, rgba(255,255,255,0.65) 48%, rgba(255,255,255,0.4) 65%, rgba(255,255,255,0.2) 80%, rgba(255,255,255,0.1) 95%)",
    // Override colors
    blue50: "#020418",
    blue100: "#070B28",
    blue200: "#0F1331",
    blue300: "#1E2457",
    blue400: "#3C4992",
    blue500: "#4E5DBC",
    blue600: "#6B7FFF",
    blue700: "#909FFF",
    blue800: "#BAC3FF",
    blue900: "#E2E6FD",
    gray50: "#101219",
    gray100: "#181921",
    gray200: "#212536",
    gray300: "#34384E",
    gray400: "#4D5270",
    gray500: "#6D6D88",
    gray600: "#AAAABF",
    gray700: "#D5D5E8",
    gray800: "#EAEAF6",
    gray900: "#F6F6FE",
  },
});

export const nobleDarkThemeClass = createTheme(themeVars, {
  ...commonVars,
  colors: {
    ...colors,
    accent: colors.primary400,
    accentText: `#EEF2F8`,
    primary: `#6B7FFF`,
    body: `#020418`,
    background: colors.gray800,
    link: colors.blue300,
    linkHover: colors.blue400,
    text: `#FFF`,
    textMuted: "#CCCCCC",
    textInverse: `#FFF`,
    textSecondary: `#909FFF`,
    textDanger: colors.red400,
    textWarning: colors.orange200,
    textSuccess: `#8BD9A6`,
    textPlaceholder: `#8895A3`,
    rewardBg: `#2F4139`,
    rewardContent: `#AEFFAB`,
    cardBg: `#0F1331`,
    inputBorder: `#1E2457`,
    inputBorderFocus: `#3C4992`,
    inputBg: `#0F1331`,
    inputDangerBorder: `#FFD0D0`,
    inputDangerBg: `#E17171`,
    inputDisabledBg: `#A7B4C2`,
    inputDisabledText: `#697584`,
    progressBg: `#1E2457`,
    progressValue: `#6B7FFF`,
    progressCursor: `#EEF2F8`,
    divider: `#1E2457`,
    menuItemBg: `#1D2024`,
    menuItemBgSelected: `#2e3339`,
    menuItemBgHovered: `#25292E`,
    menuItemBgActive: `#2C3137`,
    skeletonBg: `#3B434D`,
    trackBg: `#49525E`,
    tagButtonText: `#DAD5E3`,
    tagButtonBg: `#202023`,
    tagButtonBgHovered: `#27272B`,
    // ==== MeshButton
    meshButtonSolidPrimaryBg: `#DAD5E3`,
    meshButtonSolidPrimaryBgHovered: `#E2E2E2`,
    meshButtonSolidPrimaryText: `#0E0E0F`,
    meshButtonSolidSecondaryBg: `#1E1E1F`,
    meshButtonSolidSecondaryBgHovered: `#1E1E1F`,
    meshButtonSolidSecondaryText: `#DAD5E3`,
    meshButtonGhostText: `#85858E`,
    meshButtonGhostTextHovered: `#E1DBEB`,
    // ==== MeshStakingSliderInfo
    meshStakingSliderInfoPrimaryText: `#E2E2E2`,
    meshStakingSliderInfoSecondaryText: `#85858E`,
    meshStakingSliderInfoSecondaryTextActive: `#C0EEA4`,
    // ==== MeshTab
    meshTabText: `#E2E2E2`,
    meshTableCellText: `#E2E2E2`,
    // ==== MeshTableHeaderAction
    meshTableHeaderActionText: colors.white,
    meshTableHeaderActionSecondaryText: `#E2E2E2`,
    meshTableCellBorder: `#131313`,
    // ====
    overflowShadowBg:
      "linear-gradient(to bottom, rgba(29, 32, 36, 0), rgba(29, 32, 36, 0.6))",
    // Override colors
    blue50: "#020418",
    blue100: "#070B28",
    blue200: "#0F1331",
    blue300: "#1E2457",
    blue400: "#3C4992",
    blue500: "#4E5DBC",
    blue600: "#6B7FFF",
    blue700: "#909FFF",
    blue800: "#BAC3FF",
    blue900: "#E2E6FD",
    gray50: "#101219",
    gray100: "#181921",
    gray200: "#212536",
    gray300: "#34384E",
    gray400: "#4D5270",
    gray500: "#6D6D88",
    gray600: "#AAAABF",
    gray700: "#D5D5E8",
    gray800: "#EAEAF6",
    gray900: "#F6F6FE",
  },
});
